import React, { useState } from 'react';
import Axios from 'axios';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPADataTable from '@/components/admin/SysadminSPA/shared/SPADataTable';
import { SPAThemeScope } from '@/components/admin/SysadminSPA/shared/SPAThemeScope';
import csrfToken from '@/components/admin/SysadminSPA/shared/adminCsrf';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import cn from '@/utils/cn';
import type { CopiedSlide } from './CopySlideDialog';
import slideColumns from './slideColumns';
import { reorderUrl } from './slideUrls';
import type { SlideRow, SlideTableContext } from './types';

interface SlideShowSlidesTableProps {
  context: SlideTableContext;
  slides: SlideRow[];
  stafferId: number;
  statuses: string[];
}

const rowId = (slide: SlideRow) => slide.id;
const rowLabel = (slide: SlideRow) => slide.title;

const renumber = (rows: SlideRow[]) => rows.map((row, index) => ({ ...row, position: index + 1 }));

// A fresh copy has no copies of its own yet, so it is always deletable; its admin notes were copied
// server-side, so the row leaves them undefined and the notes cell fetches them.
const copiedRow = (source: SlideRow, copy: CopiedSlide): SlideRow => ({
  ...source, deletable: true, id: copy.id, notes: undefined, title: copy.title || 'Slide',
});

const SlidesTable = ({
  context, slides, stafferId, statuses,
}: SlideShowSlidesTableProps) => {
  const [rows, setRows] = useState(slides);
  const [notesExpanded, setNotesExpanded] = useState(false);

  const insertCopy = (source: SlideRow, copy: CopiedSlide) => setRows((current) => {
    const at = current.findIndex(row => row.id === source.id) + 1;

    return renumber([...current.slice(0, at), copiedRow(source, copy), ...current.slice(at)]);
  });

  const toggleHidden = (slide: SlideRow) => setRows(current => current.map(row => (
    row.id === slide.id ? { ...row, hidden: !row.hidden } : row
  )));

  const removeRow = (slide: SlideRow) => setRows(current => renumber(current.filter(row => row.id !== slide.id)));

  const columns = slideColumns({
    context, notesExpanded, onCopied: insertCopy, onDeleted: removeRow, onToggled: toggleHidden, stafferId, statuses,
  });

  // Same payload the jQuery sortable posted: the slide ids in their new order.
  const reorder = (ordered: SlideRow[]) => {
    setRows(renumber(ordered));

    return Axios.post(reorderUrl(context), { slide: ordered.map(rowId) }, { headers: { 'X-CSRF-Token': csrfToken() } });
  };

  return (
    <SPADataTable
      striped
      columns={columns}
      data={rows}
      defaultSort={[]}
      emptyMessage="No slides yet."
      reorder={{ getRowId: rowId, getRowLabel: rowLabel, onReorder: reorder }}
      secondaryActionSlot={rows.length > 0 && (
        <SPAButton size="xs" onClick={() => setNotesExpanded(current => !current)}>
          {notesExpanded ? 'Collapse all notes' : 'Expand all notes'}
        </SPAButton>
      )}
    />
  );
};

// AIDEV-NOTE: SPA components on the legacy Edit Presentation page, mounted the way admin/UserSearch
// is: tokens pinned light to match the page, SPAThemeScope for the portalled dialogs. One root for
// the whole table, so every row's notes share the query cache.
const THEME_CLASS = 'spa-theme-light';

const SlideShowSlidesTable = (props: SlideShowSlidesTableProps) => (
  <ReactQueryProvider>
    <SPAThemeScope className={THEME_CLASS}>
      <div className={cn(THEME_CLASS, 'tw-font-tci tw-mt-4')}>
        <SlidesTable {...props} />
      </div>
    </SPAThemeScope>
  </ReactQueryProvider>
);

export default SlideShowSlidesTable;
